<template>
    <div v-show="!incorrectAA && isLoaded" class="tab-pane active">
        <div v-show="hasGroups" class="profiles-with-navigation no-print">
            <div class="common-nav-wrap">
                <Simplebar class="with-border-track">
                    <div class="common-nav">
                        <Sidebar
                            :parent-name="ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS"
                            :list="mappedAccessGroups"
                            :current-id="currentAccessGroupId"
                            :search-placeholder="$t('wanderManagement.accessGroupsTab.searchPlaceholder')"
                            :add-new-text="$t('wanderManagement.accessGroupsTab.addNewWirelessAccessGroup')"
                            @delete="deleteById"
                        />
                    </div>
                </Simplebar>
            </div>
            <div v-if="isLoaded" class="component-with-nav-body">
                <AccessGroupsForm
                    v-model="accessGroup"
                    :exit-detection-options="exitDetection"
                    :always-access-options="alwaysAccess"
                    :conditional-access-options="conditionalAccess"
                    :wander-alarm-options="wanderAlarm"
                    :loiter-alarm-options="loiterAlarm"
                    :forbidden-names="forbiddenNames"
                />
            </div>
        </div>
        <div v-if="!hasGroups" class="data-empty">
            <h3>{{ $t('wanderManagement.accessGroupsTab.noAccessGroups') }}</h3>
        </div>
        <div class="print-only">
            <h4 v-if="showPrintKeyword">{{ $t('print.search', { val: searchKeyword }) }}</h4>
            <table v-if="accessGroupsCopy.length > 0" class="table print-access">
                <thead>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.name')"></th>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.comments')"></th>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.alwaysAccess')"></th>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.conditionalAccess')"></th>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.wanderAlarm')"></th>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.loiterAlarm')"></th>
                    <th v-text="$t('wanderManagement.accessGroupsTab.accessGroup.exitDetection')"></th>
                </thead>
                <tbody>
                    <tr v-for="(group, index) in filteredAccessGroupsCopy" :key="index">
                        <td v-text="group.name"></td>
                        <td v-text="group.comment"></td>
                        <td v-text="accessString(group.always_access)"></td>
                        <td v-text="accessString(group.conditional_access)"></td>
                        <td>
                            <div v-for="(alarm, index2) in group.wander_alarm" :key="index2">
                                <span v-text="alarm.name"></span>
                                <span>
                                    ({{ $t('wanderManagement.accessGroupsTab.accessGroup.durationPrint') }}:
                                    {{ alarm.duration ? alarm.duration : '\u221E' }})
                                </span>
                            </div>
                        </td>
                        <td>
                            <div v-for="(alarm, index2) in group.loiter_alarm" :key="index2">
                                <span v-text="alarm.name"></span>
                                <span>
                                    ({{ $t('wanderManagement.accessGroupsTab.accessGroup.delayPrint') }}:
                                    {{ alarm.delay }},
                                    {{ $t('wanderManagement.accessGroupsTab.accessGroup.durationPrint') }}:
                                    {{ alarm.duration ? alarm.duration : '\u221E' }})
                                </span>
                            </div>
                        </td>
                        <td v-text="accessString(group.exit_detection)"></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import Sidebar from '../common/Sidebar';
import AccessGroupsForm from './accessgroups/AccessGroupsForm';
import fixingEditForm from '../common/fixingEditForm';
import mappingState from '../common/mappingState';
import { mapState, mapMutations } from 'vuex';
import some from 'lodash-es/some';
import Simplebar from 'simplebar-vue';
import {
    resourceAccessGroupsWithLocations,
    resourceAccessGroups,
    resourceWirelessDevice,
    resourceAccessGroupsLocations,
} from '../../api/resources';
import { SUCCESS, ERROR, DEFAULT_DELAY, ROUTE_NAMES, USER_TYPES } from '../../constants';
import { getCancelToken } from '../../api/index';
import { errorHandler, sortLocations, ifIsNotCanceled } from '../common/helpers';
import { deleteHandler } from '../common/deleteHandler';
import cloneDeep from 'lodash-es/cloneDeep';

export default {
    components: {
        Sidebar,
        Simplebar,
        AccessGroupsForm,
    },

    mixins: [fixingEditForm, mappingState],

    constants: {
        ROUTE_NAMES: ROUTE_NAMES,
    },

    data() {
        return {
            hasGroups: false,
            showPrintKeyword: false,
            currentAccessGroupId: null,
            accessGroup: {
                wireless_access_id: '',
                name: '',
                comment: '',
                exit_detection: [],
                always_access: [],
                conditional_access: [],
                wander_alarm: [],
                loiter_alarm: [],
            },
            accessGroups: [],
            accessGroupsCopy: [],
            wirelessModules: [],
            wirelessIds: [],
            locations: [],
            exitDetection: [],
            alwaysAccess: [],
            conditionalAccess: [],
            wanderAlarm: [],
            loiterAlarm: [],
            isLoaded: false,
            incorrectAA: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['isFormValid', 'unsavedChanges']),

        forbiddenNames() {
            let forbiddenNames = [];
            if (!this.accessGroup.hasOwnProperty('wireless_access_id')) {
                return this.accessGroups;
            }
            for (let key in this.accessGroups) {
                if (
                    this.accessGroups.hasOwnProperty(key) &&
                    this.accessGroup.wireless_access_id !== this.accessGroups[key].wireless_access_id
                ) {
                    forbiddenNames.push({ name: this.accessGroups[key].name });
                }
            }
            return forbiddenNames;
        },

        filteredAccessGroupsCopy() {
            return this.showPrintKeyword
                ? this.accessGroupsCopy.filter(item => item.name.toLowerCase().includes(this.searchKeyword))
                : this.accessGroupsCopy;
        },

        /**
         * Unifies array of access groups for proper render in sidebar
         * @returns {Array|{id, name, isUsed: *}[]}
         */
        mappedAccessGroups() {
            return this.accessGroups.map(i => ({
                id: i.wireless_access_id,
                name: i.name,
                isUsed: this.isAccessGroupUsed(i.wireless_access_id),
            }));
        },
    },

    watch: {
        accessGroup: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },

        $route(to) {
            this.setMode(to);
        },

        searchKeyword(val) {
            this.showPrintKeyword =
                val && this.accessGroupsCopy.some(item => item.name.toLowerCase().includes(val));
        },

        accessGroupsCopy: {
            handler(list) {
                let keyword = this.searchKeyword;
                this.showPrintKeyword =
                    keyword && list.some(item => item.name.toLowerCase().includes(keyword));
            },
            deep: true,
        },
    },

    created() {
        this.showLoading(true);
        // Event listener for edit form styles
        window.addEventListener('resize', this.checkHeightOnResize);
        this.updateData(() => {
            this.undo();
            this.showLoading(false);
            return Promise.resolve();
        });
        return this.loadWirelessModules()
            .then(this.loadLocations)
            .then(this.loadAccessGroups)
            .then(() => {
                if (this.userType === USER_TYPES.SUPER_USER && this.incorrectAA) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t('error.common.forbidden'),
                    });
                }
                this.setMode(this.$route);
                this.isLoaded = true;
                this.showLoading(false);
            })
            .catch(
                ifIsNotCanceled({
                    callback: response => {
                        errorHandler(response.status, ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS);
                        return response;
                    },
                })
            );
    },

    beforeDestroy() {
        this.cancelToken.cancel();
        window.removeEventListener('resize', this.checkHeightOnResize);
        this.setFormValid(true);
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        /**
         * Prepares access group object for saving into database
         * @param accessGroup
         * @returns string
         */
        prepareGroupForSave(accessGroup) {
            return JSON.stringify({
                name: accessGroup.name,
                comment: accessGroup.comment,
                always_access: (accessGroup.always_access || []).map(obj => ({
                    location_id: obj.location_id,
                })),
                conditional_access: (accessGroup.conditional_access || []).map(obj => ({
                    location_id: obj.location_id,
                })),
                exit_detection: (accessGroup.exit_detection || []).map(obj => ({
                    location_id: obj.location_id,
                })),
                wander_alarm: (accessGroup.wander_alarm || []).map(obj => ({
                    location_id: obj.value,
                    duration: obj.duration ? obj.duration : null,
                })),
                loiter_alarm: (accessGroup.loiter_alarm || []).map(obj => ({
                    location_id: obj.value,
                    delay: obj.delay ? obj.delay : null,
                    duration: obj.duration ? obj.duration : null,
                })),
            });
        },

        save() {
            this.showLoading(true);
            this.accessGroup.name = this.accessGroup.name.trim();
            this.accessGroup.comment = this.accessGroup.comment.trim();
            return resourceAccessGroups({
                method: 'post',
                id: this.accessGroup.wireless_access_id ? this.accessGroup.wireless_access_id : null,
                data: this.prepareGroupForSave(this.accessGroup),
            })
                .then(response => {
                    let jsonData = response.data;
                    this.popup({
                        type: SUCCESS,
                        header: this.$t('common.success'),
                        text: this.$t('common.saved'),
                    });
                    return this.loadAccessGroups().then(() => {
                        this.showLoading(false);
                        this.setUnsavedChanges(false);
                        // redirect to newly added
                        this.$nextTick(() => {
                            if (!this.accessGroup.wireless_access_id) {
                                if (jsonData && jsonData.wireless_access_id) {
                                    this.$router.replace({
                                        name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__EDIT,
                                        params: {
                                            id: jsonData.wireless_access_id,
                                        },
                                    });
                                }
                            }
                        });
                    });
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            if (response.status === 404 || response.status === 409) {
                                return this.loadAccessGroups().then(() => {
                                    this.setUnsavedChanges(false);
                                    this.$nextTick(() => {
                                        this.$router.replace({
                                            name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS,
                                        });
                                    });
                                });
                            }
                            return Promise.resolve();
                        },
                    })
                );
        },

        deleteById(id) {
            let deletedAccessGroup = this.accessGroups.find(item => item.wireless_access_id === id);
            let confirmPart = {
                confirmTitle: this.$t('wanderManagement.accessGroupsTab.removingAccessGroupTitle'),
                confirmBody: this.$t('wanderManagement.accessGroupsTab.removingAccessGroup', {
                    name: deletedAccessGroup ? deletedAccessGroup.name : '',
                }),
            };
            return deleteHandler(
                resourceAccessGroups,
                id,
                this.loadAccessGroups,
                ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS,
                confirmPart
            );
        },

        /**
         * Remove non-existent locations from access groups
         */
        getAGWithoutNonexistentLocations(data) {
            let d = data.map(accessGroup => {
                let newAccessGroup = { ...accessGroup };
                [
                    'always_access',
                    'conditional_access',
                    'wander_alarm',
                    'loiter_alarm',
                    'exit_detection',
                ].forEach(key => {
                    newAccessGroup[key] = Array.isArray(accessGroup[key])
                        ? this.filterAndaddTitle(accessGroup[key], key)
                        : accessGroup[key];
                });
                return newAccessGroup;
            });
            return d;
        },

        filterAndaddTitle(group, key) {
            let filtred = group.filter(({ location_id }) => some(this.locations[key], { location_id }));
            filtred.forEach(location => {
                location.title = location.module
                    ? location.name +
                      '\n' +
                      this.$t('wanderManagement.accessGroupsTab.accessModuleTitle', {
                          module: location.module,
                      })
                    : location.name;
            });
            return filtred;
        },

        loadAccessGroups() {
            return resourceAccessGroupsWithLocations({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    let data = response.data ? sortLocations(response.data, 'name') : [];
                    this.accessGroups = this.getAGWithoutNonexistentLocations(data);
                    this.accessGroupsCopy = cloneDeep(this.accessGroups);
                })
                .then(() => {
                    this.setLocationGroups();
                })
                .catch(response => {
                    this.accessGroupsCopy = [];

                    return Promise.reject(response);
                });
        },

        loadWirelessModules() {
            return resourceWirelessDevice({
                cancelToken: this.cancelToken.token,
            }).then(response => {
                this.wirelessModules = response.data ? response.data : [];

                let filteredModules = this.wirelessModules.filter(item => item.access_group !== null);
                this.wirelessIds = filteredModules.map(item => item.access_group.wireless_access_id);
            });
        },

        loadLocations() {
            this.incorrectAA = false;
            return resourceAccessGroupsLocations({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.locations = response.data ? response.data : [];
                    if (this.locations.assignmentarea_id !== this.userAreaId) {
                        this.incorrectAA = true;
                    } else {
                        this.setLocationGroups();
                    }
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response =>
                            errorHandler(response.status, ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS),
                    })
                );
        },

        /**
         * Prepares lists for multiselects' options in add/edit form
         */
        setLocationGroups() {
            if (this.locations.exit_detection) {
                this.exitDetection = sortLocations(
                    this.locations.exit_detection.map(item => ({
                        location_id: item.location_id,
                        name: item.name,
                    }))
                );
            }
            if (this.locations.always_access) {
                this.alwaysAccess = sortLocations(
                    this.locations.always_access.map(item => ({
                        location_id: item.location_id,
                        name: item.name,
                        title: item.module
                            ? item.name +
                              '\n' +
                              this.$t('wanderManagement.accessGroupsTab.accessModuleTitle', {
                                  module: item.module,
                              })
                            : item.name,
                    }))
                );
            }
            if (this.locations.conditional_access) {
                this.conditionalAccess = sortLocations(
                    this.locations.conditional_access.map(item => ({
                        location_id: item.location_id,
                        name: item.name,
                        title: item.module
                            ? item.name +
                              '\n' +
                              this.$t('wanderManagement.accessGroupsTab.accessModuleTitle', {
                                  module: item.module,
                              })
                            : item.name,
                    }))
                );
            }
            if (this.locations.wander_alarm) {
                this.wanderAlarm = this.locations.wander_alarm.map(this.mapWanderAlarm);
            }
            if (this.locations.loiter_alarm) {
                this.loiterAlarm = this.locations.loiter_alarm.map(this.mapLoiterAlarm);
            }
        },

        /**
         * Modifies each wander alarm object from database to required form for select-box
         * @param alarm
         * @returns {{value: *, name: string, duration: *}}
         */
        mapWanderAlarm(alarm) {
            return {
                value: alarm.location_id,
                name:
                    alarm.name +
                    (alarm.door
                        ? ' (' + this.$t('wanderManagement.accessGroupsTab.accessGroup.door') + ')'
                        : ''),
                duration: alarm.duration,
                title: alarm.module
                    ? alarm.name +
                      '\n' +
                      this.$t('wanderManagement.accessGroupsTab.accessModuleTitle', {
                          module: alarm.module,
                      })
                    : alarm.name,
            };
        },

        /**
         * Modifies each loiter alarm object from database to required form for select-box
         * @param alarm
         * @returns {{value: *, name: string, duration: *}}
         */
        mapLoiterAlarm(alarm) {
            return {
                value: alarm.location_id,
                name:
                    alarm.name +
                    (alarm.door
                        ? ' (' + this.$t('wanderManagement.accessGroupsTab.accessGroup.door') + ')'
                        : ''),
                delay: alarm.delay ? alarm.delay : DEFAULT_DELAY,
                duration: alarm.duration,
                title: alarm.module
                    ? alarm.name +
                      '\n' +
                      this.$t('wanderManagement.accessGroupsTab.accessModuleTitle', {
                          module: alarm.module,
                      })
                    : alarm.name,
            };
        },

        hasAccessGroups() {
            if (this.accessGroups.length) {
                this.hasGroups = true;
            } else {
                if (this.$route.name === ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__ADD) {
                    this.hasGroups = true;
                } else this.hasGroups = this.$route.name !== ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS;
            }
        },
        /**
         * Prepare groups for print version
         */
        accessString(agr) {
            let names = '';
            if (agr) {
                agr.forEach(gr => (names = names + gr.name + ', '));
            }
            return names.slice(0, -2);
        },

        setCurrentAccessGroup(id) {
            let group = this.accessGroups.find(i => i.wireless_access_id === id);
            if (group) {
                this.accessGroup = {
                    ...group,
                    wander_alarm: (group.wander_alarm || []).map(this.mapWanderAlarm),
                    loiter_alarm: (group.loiter_alarm || []).map(this.mapLoiterAlarm),
                };
            } else {
                this.popup({
                    type: ERROR,
                    header: this.$t('common.error'),
                    text: this.$t('wanderManagement.accessGroupsTab.notFound'),
                });
                errorHandler(404, ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS);
            }
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        setEmptyAccessGroup() {
            this.accessGroup = {
                wireless_access_id: '',
                name: '',
                comment: '',
                exit_detection: [],
                always_access: [],
                conditional_access: [],
                wander_alarm: [],
                loiter_alarm: [],
            };
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        undo() {
            let id = this.currentAccessGroupId;
            if (id) {
                this.setCurrentAccessGroup(id);
            } else {
                this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS });
            }
        },

        initAdding() {
            this.setEmptyAccessGroup();
            this.setAddingButtons({
                cancel: () => {
                    this.setUnsavedChanges(false);
                    this.$nextTick(() => {
                        this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS });
                    });
                },
                save: () => {
                    if (this.isFormValid) {
                        this.save();
                    }
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid || this.incorrectAA,
            });
        },

        initEditing(id) {
            this.setEditingButtons({
                add: () => this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__ADD }),
                undo: () => this.undo(),
                wasChanged: () => !this.unsavedChanges,
                save: () => {
                    if (this.isFormValid) {
                        this.save();
                    }
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid || this.incorrectAA,
                addDisabled: () => this.incorrectAA,
            });
            this.setCurrentAccessGroup(id);
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        setMode(route) {
            this.hasAccessGroups();
            if (route.name === ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS) {
                if (this.accessGroups.length) {
                    let firstId = this.accessGroups[0].wireless_access_id;
                    this.$router.replace({
                        name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__EDIT,
                        params: { id: firstId },
                    });
                    this.initEditing(firstId);
                } else {
                    this.configButton({
                        buttonName: 'addButton',
                        action: () =>
                            this.$router.push({ name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__ADD }),
                        disabled: () => this.incorrectAA,
                    });
                }
            } else if (route.name === ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS__ADD) {
                this.currentAccessGroupId = null;
                this.initAdding();
            } else {
                if (
                    this.accessGroups.length &&
                    this.accessGroups.some(s => s.wireless_access_id === Number(route.params.id))
                ) {
                    this.currentAccessGroupId = Number(route.params.id);
                    this.initEditing(this.currentAccessGroupId);
                } else {
                    this.$router.replace({
                        name: ROUTE_NAMES.WANDER_MANAGEMENT__ACCESS_GROUPS,
                    });
                }
            }
            // Checks height of elements for their initial state
            this.$nextTick(() => {
                this.checkHeightOnResize();
            });
        },

        isAccessGroupUsed(id) {
            return this.wirelessIds.includes(id);
        },
    },
};
</script>
